// Hand-authored API reference (content-as-data). Add an endpoint by adding an
// object here; keep examples in sync with the api/v1 serializers.

export const BASE =
  process.env.REACT_APP_API_DOCS_BASE_URL ||
  "https://dashboard.emersoncoaching.com.au/api/v1";

export const AUTH_EXAMPLE = `curl ${BASE}/me \\
  -H "Authorization: Bearer em_live_your_key_here"`;

export const groups = [
  {
    id: "account",
    title: "Account",
    endpoints: [
      {
        id: "get-me",
        method: "GET",
        path: "/me",
        title: "Your user",
        description: "Returns the user that owns the API key.",
        request: `curl ${BASE}/me \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{
  "user": {
    "id": 12,
    "name": "Dan Dobos",
    "email": "dan@emersoncoaching.com.au",
    "roles": ["admin"],
    "region": "VIC",
    "is_locked_out": false,
    "last_activity_at": "2026-07-08T02:14:00+10:00"
  },
  "api_key": { "name": "Claude", "scope": "read_write" }
}`
      }
    ]
  },
  {
    id: "users",
    title: "Users",
    endpoints: [
      {
        id: "get-users",
        method: "GET",
        path: "/users",
        title: "List users",
        description: "Active coaches / sales people.",
        params: [
          { name: "roles", type: "string", description: "Comma-separated role filter, e.g. coach,admin" },
          { name: "text_filter", type: "string", description: "Match on name / email / phone" },
          { name: "limit", type: "integer", description: "Page size (max 100, default 20)" },
          { name: "offset", type: "integer", description: "Rows to skip (default 0)" }
        ],
        request: `curl "${BASE}/users?roles=tcs_coach&limit=20" \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{
  "users": [
    { "id": 8, "name": "Sam Coach", "email": "sam@emersoncoaching.com.au", "roles": ["tcs_coach"], "region": "VIC" }
  ],
  "meta": { "total": 1, "limit": 20, "offset": 0 }
}`
      }
    ]
  },
  {
    id: "deals",
    title: "Deals",
    endpoints: [
      {
        id: "get-deals",
        method: "GET",
        path: "/deals",
        title: "List deals",
        description: "The pipeline. Filter by view and/or a free-text search.",
        params: [
          { name: "view", type: "string", description: "open | verification | upgrade | followup | review | on_hold | completed" },
          { name: "text_filter", type: "string", description: "Match on contact name / email / mobile" },
          { name: "limit", type: "integer", description: "Page size (max 100, default 20)" },
          { name: "offset", type: "integer", description: "Rows to skip (default 0)" }
        ],
        request: `curl "${BASE}/deals?view=open&limit=20" \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{
  "deals": [
    {
      "id": 4821,
      "flags": ["tcs_booked", "tcs_attendee_confirmed"],
      "business_stream": "coaching",
      "tcs_at": "2026-07-10T17:00:00+10:00",
      "tcs_assigned_to": "sam@emersoncoaching.com.au",
      "value_of_sale": null,
      "on_hold": false,
      "contact": { "id": 991, "first_name": "Jane", "last_name": "Doe", "student_first_name": "Milo", "email": "jane@example.com", "mobile": "0400111222" },
      "created_at": "2026-07-01T09:12:00+10:00"
    }
  ],
  "meta": { "total": 1, "limit": 20, "offset": 0 }
}`
      },
      {
        id: "get-deal",
        method: "GET",
        path: "/deals/:id",
        title: "Get a deal",
        description: "Full detail for one deal, including the embedded contact and sale/verification fields.",
        request: `curl ${BASE}/deals/4821 \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{
  "deal": {
    "id": 4821,
    "flags": ["tcs_booked", "tcs_attendee_confirmed"],
    "tcs_at": "2026-07-10T17:00:00+10:00",
    "tcs_assigned_to": "sam@emersoncoaching.com.au",
    "notes_admin": "Confirmed for Thursday",
    "contact": { "id": 991, "first_name": "Jane", "email": "jane@example.com", "mobile": "0400111222" }
  }
}`
      },
      {
        id: "get-transcript",
        method: "GET",
        path: "/deals/:id/transcript",
        title: "Get transcripts",
        description: "TCS Zoom transcripts (consultant name redacted), call transcriptions, and the AI sales report if generated.",
        request: `curl ${BASE}/deals/4821/transcript \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{
  "deal_id": 4821,
  "zoom_transcripts": [
    { "recording_id": "abc123", "topic": "TCS - Jane", "recorded_at": "2026-07-10T17:03:00+10:00", "transcript": "Consultant: Welcome..." }
  ],
  "call_transcriptions": [
    { "id": 55, "direction": "out", "at": "2026-07-09T11:00:00+10:00", "transcription": "Hi Jane, just confirming..." }
  ],
  "ai_sales_report": { "id": 3, "analysis_reports": [] }
}`
      },
      {
        id: "post-deal",
        method: "POST",
        path: "/deals",
        title: "Create a deal",
        description: "Creates (or finds by mobile) a contact and a deal linked to it. Attributed to your key's user.",
        params: [
          { name: "first_name", type: "string", description: "Parent first name", required: true },
          { name: "mobile", type: "string", description: "Parent mobile", required: true },
          { name: "email", type: "string", description: "Parent email" },
          { name: "student_first_name", type: "string", description: "Student first name" },
          { name: "region", type: "string", description: "e.g. VIC" },
          { name: "business_stream", type: "string", description: "e.g. coaching" },
          { name: "tcs_at", type: "datetime", description: "ISO8601 TCS time, if booking one" }
        ],
        request: `curl -X POST ${BASE}/deals \\
  -H "Authorization: Bearer em_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"first_name":"Jane","mobile":"0400111222","email":"jane@example.com","student_first_name":"Milo","region":"VIC"}'`,
        response: `{
  "deal": { "id": 4822, "flags": [], "contact": { "id": 992, "first_name": "Jane", "mobile": "0400111222" } }
}`
      },
      {
        id: "post-note",
        method: "POST",
        path: "/deals/:id/notes",
        title: "Add a note",
        description: "Appends a note to the deal history, attributed to your key's user.",
        params: [
          { name: "note", type: "string", description: "The note text", required: true }
        ],
        request: `curl -X POST ${BASE}/deals/4821/notes \\
  -H "Authorization: Bearer em_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"note":"Called, left a voicemail"}'`,
        response: `{ "deal": { "id": 4821, "flags": ["tcs_booked"] } }`
      },
      {
        id: "post-action",
        method: "POST",
        path: "/deals/:id/actions/:action_id",
        title: "Run a stage action",
        description:
          "Runs a whitelisted pipeline action. Allowed action_id values: book_tcs, sale_made, upgrade_made, no_sale, dead_deal, cancelled, verify, put_on_hold, take_off_hold, review_done_approved, review_done_declined, add_note, add_contact. Action-specific fields go in the JSON body.",
        params: [
          { name: "(varies by action)", type: "object", description: "e.g. no_sale takes no_sale_reason + notes_no_sale; sale_made takes value_of_sale, plan_duration, ..." }
        ],
        request: `curl -X POST ${BASE}/deals/4821/actions/no_sale \\
  -H "Authorization: Bearer em_live_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"no_sale_reason":"not_interested","notes_no_sale":"Changed their mind"}'`,
        response: `{ "deal": { "id": 4821, "flags": ["tcs_booked", "no_sale"] } }`
      }
    ]
  },
  {
    id: "contacts",
    title: "Contacts",
    endpoints: [
      {
        id: "get-contact",
        method: "GET",
        path: "/contacts/:id",
        title: "Get a contact",
        description: "One contact (parent or student).",
        request: `curl ${BASE}/contacts/991 \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{
  "contact": {
    "id": 991,
    "first_name": "Jane",
    "last_name": "Doe",
    "student_first_name": "Milo",
    "email": "jane@example.com",
    "mobile": "0400111222",
    "region": "VIC",
    "contact_type": "PARENT",
    "tags": []
  }
}`
      }
    ]
  },
  {
    id: "reports",
    title: "Reports",
    endpoints: [
      {
        id: "get-report-sales",
        method: "GET",
        path: "/reports/sales",
        title: "Sales report",
        description: "Flag counts per consultant across a TCS date window.",
        params: [
          { name: "start_date", type: "date", description: "Defaults to 30 days ago" },
          { name: "end_date", type: "date", description: "Defaults to today" }
        ],
        request: `curl "${BASE}/reports/sales?start_date=2026-06-01&end_date=2026-06-30" \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{ "start_date": "...", "end_date": "...", "deals_counted": 42, "totals": { "sam@...": { "sale_made": 5, "tcs_presented": 12 } } }`
      },
      {
        id: "get-report-funnels",
        method: "GET",
        path: "/reports/funnels",
        title: "Funnels report",
        description: "Registration -> attended -> booked -> confirmed -> presented -> sale funnel, split seminar vs TCS.",
        params: [
          { name: "date_field", type: "string", description: "Column to window on (default tcs_at)" },
          { name: "start_date", type: "date", description: "Defaults to 30 days ago" },
          { name: "end_date", type: "date", description: "Defaults to today" }
        ],
        request: `curl "${BASE}/reports/funnels?date_field=tcs_at" \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{ "deals_counted": 42, "combined_totals": { "flags": { "sale_made": 5 }, "percentages": {} } }`
      },
      {
        id: "get-report-spr",
        method: "GET",
        path: "/reports/sales_person_results",
        title: "Sales person results",
        description: "Per-consultant conversion / presentation rates plus per-deal outcomes.",
        params: [
          { name: "date_field", type: "string", description: "Column to window on (default tcs_at)" },
          { name: "start_date", type: "date", description: "Defaults to 30 days ago" },
          { name: "end_date", type: "date", description: "Defaults to today" }
        ],
        request: `curl "${BASE}/reports/sales_person_results?date_field=tcs_at" \\
  -H "Authorization: Bearer em_live_your_key_here"`,
        response: `{ "stats": { "scheduled": 42, "sales": 5 }, "users": [ { "email": "sam@...", "conversion_rate": 41.6 } ] }`
      }
    ]
  }
];

export const comingSoon = [
  "Seminars & registrations",
  "Leads / follow-ups",
  "Bulk messaging & SMS",
  "Webhooks"
];
